import { useMemo, useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import {
  useDocuments,
  useFolders,
  useGroups,
  useMembers,
  usePermissionRows,
  useRemovePermission,
  useSetPermission,
} from '../api/rooms'
import {
  NO_PERMISSIONS,
  type NodeType,
  type PermissionSet,
  type PrincipalType,
  type RoomResponse,
} from '../api/types'
import { FolderTree } from '../components/FolderTree'
import { PermissionChips, PermissionEditor } from '../components/PermissionEditor'
import { errorMessage, formatUtc } from '../lib/format'

/**
 * Pick a node (the room, a folder, a document), see every explicit row on it and on
 * its ancestors, and set or remove a row. What a principal actually gets is the
 * server's decision; this screen only edits the rows it decides from.
 */
export function Permissions() {
  const room = useOutletContext<RoomResponse>()
  const { roomId = '' } = useParams()
  const [folderId, setFolderId] = useState<string | null>(null)
  const [documentId, setDocumentId] = useState<string | null>(null)

  const folders = useFolders(roomId)
  const documents = useDocuments(roomId, folderId)

  const node: { type: NodeType; id: string; label: string } = documentId
    ? {
        type: 'Document',
        id: documentId,
        label: documents.data?.find((d) => d.id === documentId)?.name ?? 'Document',
      }
    : folderId
      ? {
          type: 'Folder',
          id: folderId,
          label: folders.data?.find((f) => f.id === folderId)?.name ?? 'Folder',
        }
      : { type: 'Room', id: room.id, label: room.name }

  return (
    <div className="room-grid">
      <aside className="panel room-side">
        {folders.data && (
          <FolderTree
            folders={folders.data}
            selected={folderId}
            onSelect={(id) => {
              setFolderId(id)
              setDocumentId(null)
            }}
            rootLabel={`Whole room: ${room.name}`}
          />
        )}
        {documents.data && documents.data.length > 0 && (
          <div className="tree-docs">
            <p className="small muted">Documents here</p>
            <ul className="tree-level">
              {documents.data.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    className={documentId === d.id ? 'tree-item tree-item-selected' : 'tree-item'}
                    onClick={() => setDocumentId(d.id)}
                  >
                    <span className="tree-name">{d.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </aside>

      <section className="stack room-main">
        <h2 className="h2 no-margin">
          Permissions on <span className="chip">{node.type}</span> {node.label}
        </h2>
        <NodePermissions roomId={roomId} room={room} nodeType={node.type} nodeId={node.id} />
      </section>
    </div>
  )
}

function NodePermissions({
  roomId,
  room,
  nodeType,
  nodeId,
}: {
  roomId: string
  room: RoomResponse
  nodeType: NodeType
  nodeId: string
}) {
  const rows = usePermissionRows(roomId, nodeType, nodeId)
  const remove = useRemovePermission(roomId)
  const canManage = room.memberRole === 'Owner' || room.permissions.managePermissions

  if (rows.isPending) return <p className="muted small">Loading…</p>
  if (rows.isError)
    return (
      <div className="alert alert-error" role="alert">
        <p>{errorMessage(rows.error)}</p>
      </div>
    )

  const here = rows.data.filter((r) => !r.isInherited)
  const inherited = rows.data.filter((r) => r.isInherited)

  return (
    <div className="stack">
      <div className="panel">
        <h3 className="h3">Set here</h3>
        {here.length === 0 && (
          <p className="muted small">No explicit rows on this {nodeType.toLowerCase()}.</p>
        )}
        {here.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>Principal</th>
                <th>Permissions</th>
                <th>Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {here.map((r) => (
                <tr key={r.id} className={r.isExpired ? 'row-expired' : undefined}>
                  <td>
                    <span className="chip">{r.principalType}</span>{' '}
                    {r.principalName ?? r.principalId}
                    {r.principalEmail && <span className="small muted"> {r.principalEmail}</span>}
                  </td>
                  <td>
                    <PermissionChips value={r.permissions} />
                  </td>
                  <td className="small muted">
                    {r.expiresUtc ? formatUtc(r.expiresUtc) : '—'}
                    {r.isExpired && <span className="chip chip-bad">expired: denies</span>}
                  </td>
                  <td className="td-actions">
                    {canManage && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={remove.isPending}
                        onClick={() =>
                          remove.mutate({
                            nodeType,
                            nodeId,
                            principalType: r.principalType,
                            principalId: r.principalId,
                          })
                        }
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {remove.isError && <p className="error-text small">{errorMessage(remove.error)}</p>}
      </div>

      {inherited.length > 0 && (
        <div className="panel">
          <h3 className="h3">Inherited from above</h3>
          <p className="small muted">
            A row set here for the same principal replaces its inherited row completely; rows for
            other principals keep applying.
          </p>
          <table className="table">
            <thead>
              <tr>
                <th>From</th>
                <th>Principal</th>
                <th>Permissions</th>
                <th>Expires</th>
              </tr>
            </thead>
            <tbody>
              {inherited.map((r) => (
                <tr key={r.id} className={r.isExpired ? 'row-expired' : undefined}>
                  <td className="small">
                    <span className="chip">{r.nodeType}</span>{' '}
                    {r.nodeIndex ? `${r.nodeIndex} ` : ''}
                    {r.nodeName}
                  </td>
                  <td>
                    <span className="chip">{r.principalType}</span>{' '}
                    {r.principalName ?? r.principalId}
                  </td>
                  <td>
                    <PermissionChips value={r.permissions} />
                  </td>
                  <td className="small muted">{r.expiresUtc ? formatUtc(r.expiresUtc) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canManage && room.status !== 'Frozen' && room.status !== 'Archived' && (
        <SetRow roomId={roomId} nodeType={nodeType} nodeId={nodeId} />
      )}
    </div>
  )
}

function SetRow({
  roomId,
  nodeType,
  nodeId,
}: {
  roomId: string
  nodeType: NodeType
  nodeId: string
}) {
  const members = useMembers(roomId)
  const groups = useGroups(roomId)
  const set = useSetPermission(roomId)
  const [principal, setPrincipal] = useState('')
  const [bits, setBits] = useState<PermissionSet>({ ...NO_PERMISSIONS, view: true })
  const [expires, setExpires] = useState('')

  const principals = useMemo(() => {
    const list: { key: string; type: PrincipalType; id: string; label: string }[] = []
    for (const g of groups.data ?? [])
      list.push({ key: `Group:${g.id}`, type: 'Group', id: g.id, label: `Group: ${g.name}` })
    for (const m of members.data ?? [])
      if (m.memberRole === 'Participant')
        list.push({
          key: `User:${m.userId}`,
          type: 'User',
          id: m.userId,
          label: m.displayName ?? m.email ?? m.userId,
        })
    return list
  }, [groups.data, members.data])

  const chosen = principals.find((p) => p.key === principal)

  return (
    <form
      className="panel stack-sm"
      onSubmit={(e) => {
        e.preventDefault()
        if (!chosen) return
        set.mutate({
          nodeType,
          nodeId,
          body: {
            principalType: chosen.type,
            principalId: chosen.id,
            permissions: bits,
            expiresUtc: expires ? new Date(expires).toISOString() : null,
          },
        })
      }}
    >
      <h3 className="h3">Set a row here</h3>
      <p className="small muted">
        The row is the complete set for that principal on this {nodeType.toLowerCase()} and
        everything beneath it. Owners are not listed: they hold every permission already.
      </p>
      <div className="inline-form">
        <select
          value={principal}
          onChange={(e) => setPrincipal(e.target.value)}
          aria-label="Principal"
        >
          <option value="">Choose a group or participant…</option>
          {principals.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </select>
        <label className="small muted">
          Expires{' '}
          <input
            type="datetime-local"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
            aria-label="Expires"
          />
        </label>
      </div>
      <PermissionEditor value={bits} onChange={setBits} idPrefix={`set-${nodeType}-${nodeId}`} />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={!chosen || set.isPending}>
          Save row
        </button>
        {set.isError && <span className="error-text small">{errorMessage(set.error)}</span>}
        {set.isSuccess && <span className="small muted">Saved.</span>}
      </div>
    </form>
  )
}
